<template>
  <div class="row justify-content-center">
    <div class="col-lg-11 col-md-10">
      <div class="card bg-secondary shadow border-0">
        <div class="card-header bg-transparent pb-3">
          <div class="text-muted text-center mt-2 mb-3">
            <h3>Audio Processing</h3>
            <small>Process your Quran recitation audio with professional effects</small>
          </div>
        </div>

        <div class="card-body px-lg-4 py-lg-4">
          <div v-if="loadError" class="row justify-content-center">
            <div class="col-md-5">
              <div class="card border">
                <div class="card-body text-center py-5">
                  <i class="ni ni-fat-remove display-2 text-danger mb-3"></i>
                  <h4>Could not load audio tools</h4>
                  <p class="text-muted">{{ loadError }}</p>
                  <base-button type="primary" @click="loadModules" class="mt-2">Retry</base-button>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="!ready" class="text-center py-5">
            <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
            <p class="text-muted mt-3 mb-0">Loading audio tools...</p>
          </div>

          <div v-else>
            <div class="step-indicators mb-4">
              <div class="d-flex justify-content-center">
                <div class="step-item" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
                  <div class="step-circle">1</div>
                  <small>Upload Audio</small>
                </div>
                <div class="step-line" :class="{ active: currentStep > 1 }"></div>
                <div class="step-item" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
                  <div class="step-circle">2</div>
                  <small>Select Profile</small>
                </div>
                <div class="step-line" :class="{ active: currentStep > 2 }"></div>
                <div class="step-item" :class="{ active: currentStep === 3 }">
                  <div class="step-circle">3</div>
                  <small>Customize & Process</small>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 1" class="row">
              <div class="col-12">
                <div class="upload-zone" 
                     :class="{ 'drag-over': isDragging }"
                     @dragover.prevent="isDragging = true"
                     @dragleave.prevent="isDragging = false"
                     @drop.prevent="handleDrop">
                  <div class="text-center py-5">
                    <i class="ni ni-cloud-upload-96 display-3 text-primary mb-3"></i>
                    <h4>Drop your audio file here</h4>
                    <p class="text-muted">WAV, MP3, FLAC, OGG, M4A - up to 25MB</p>
                    <input type="file" ref="fileInput" @change="handleFileSelect" accept="audio/*" style="display: none">
                    <base-button type="primary" @click="$refs.fileInput.click()">
                      Select Audio File
                    </base-button>
                  </div>
                </div>

                <div v-if="audioFile" class="mt-3 p-3 bg-white rounded">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <strong>{{ audioFile.name }}</strong>
                      <span class="text-muted ml-2">({{ formatFileSize(audioFile.size) }})</span>
                    </div>
                    <base-button type="danger" size="sm" @click="removeFile">
                      <i class="fa fa-times"></i>
                    </base-button>
                  </div>
                  <audio ref="originalAudio" :src="audioUrl" controls class="w-100 mt-2"></audio>
                  <div class="mt-3 text-center">
                    <base-button type="success" @click="goToProfileStep">
                      <i class="fa fa-arrow-right"></i> Next: Choose Profile
                    </base-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 2" class="row">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h4 class="mb-0">Select Audio Profile</h4>
                  <base-button type="secondary" size="sm" @click="currentStep = 1">
                    <i class="fa fa-arrow-left"></i> Back
                  </base-button>
                </div>

                <h6 class="text-muted text-uppercase small mb-2"><i class="fa fa-microphone mr-1"></i> Reciter Presets</h6>
                <div class="row mb-4">
                  <div class="col-md-6 col-lg mb-3" v-for="(profile, key) in reciterProfiles" :key="key">
                    <div class="card h-100 profile-card" 
                         :class="{ 'border-primary selected': selectedProfileKey === key }"
                         @click="selectProfile(key)">
                      <div class="card-body text-center py-4">
                        <i class="fa fa-star display-4 mb-2" :class="selectedProfileKey === key ? 'text-primary' : 'text-muted'"></i>
                        <h5 class="mb-1">{{ profile.name }}</h5>
                        <small class="text-muted">{{ profile.description }}</small>
                        <div class="mt-2">
                          <span class="badge badge-success">Reciter</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h6 class="text-muted text-uppercase small mb-2"><i class="fa fa-sliders mr-1"></i> Basic Presets</h6>
                <div class="row mb-4">
                  <div class="col-md-6 col-lg-3 mb-3" v-for="(profile, key) in basicProfiles" :key="key">
                    <div class="card h-100 profile-card" 
                         :class="{ 'border-primary selected': selectedProfileKey === key }"
                         @click="selectProfile(key)">
                      <div class="card-body text-center py-4">
                        <i class="fa fa-headphones display-4 mb-2" :class="selectedProfileKey === key ? 'text-primary' : 'text-muted'"></i>
                        <h5 class="mb-1">{{ profile.name }}</h5>
                        <small class="text-muted">{{ profile.description }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <template v-if="aiDetectedProfiles && Object.keys(aiDetectedProfiles).length">
                  <h6 class="text-muted text-uppercase small mb-2"><i class="fa fa-magic mr-1"></i> AI Results</h6>
                  <div class="row mb-4">
                    <div class="col-12 mb-3" v-for="(profile, key) in aiDetectedProfiles" :key="key">
                      <div class="card h-100 profile-card" 
                           :class="{ 'border-primary selected': selectedProfileKey === key }"
                           @click="selectProfile(key)">
                        <div class="card-body text-center py-4">
                          <i class="fa fa-magic display-4 mb-2" :class="selectedProfileKey === key ? 'text-info' : 'text-muted'"></i>
                          <h5 class="mb-1">{{ profile.name }}</h5>
                          <small class="text-muted">{{ profile.description }}</small>
                          <div class="mt-2">
                            <span class="badge" :class="profile.category === 'AI Recommended' ? 'badge-success' : 'badge-info'">{{ profile.category }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <div class="card border mb-4">
                  <div class="card-body text-center py-4">
                    <i class="ni ni-atom display-4 text-info mb-2"></i>
                    <h5>AI Profile Detection</h5>
                    <p class="text-muted mb-3">Upload a reference audio and AI will analyze and replicate its processing chain</p>
                    <input type="file" ref="referenceInput" @change="handleReferenceSelect" accept="audio/*" style="display: none">
                    <base-button type="info" outline @click="$refs.referenceInput.click()" :disabled="detectingProfile">
                      <span v-if="detectingProfile"><i class="fa fa-spinner fa-spin"></i> Analyzing...</span>
                      <span v-else><i class="fa fa-magic"></i> Detect Profile from Audio</span>
                    </base-button>
                    <p v-if="detectError" class="text-danger mt-2 mb-0">{{ detectError }}</p>
                    <div v-if="detectedProfile" class="mt-3 text-left">
                      <div class="alert alert-info">
                        <strong>{{ detectedProfile.profile_name }}</strong>
                        <p class="mb-0">{{ detectedProfile.description }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-center mt-3" v-if="selectedProfileKey">
                  <base-button type="success" @click="goToCustomizeStep">
                    <i class="fa fa-arrow-right"></i> Next: Customize Effects
                  </base-button>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 3" class="row">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h4 class="mb-0">Customize Effects</h4>
                  <div>
                    <base-button type="secondary" size="sm" @click="currentStep = 2" class="mr-2">
                      <i class="fa fa-arrow-left"></i> Back
                    </base-button>
                    <base-button type="warning" size="sm" @click="resetToProfile" v-if="selectedProfileKey">
                      <i class="fa fa-undo"></i> Reset to Profile
                    </base-button>
                  </div>
                </div>

                <div class="profile-switcher card mb-3">
                  <div class="card-body py-2">
                    <label class="mb-2 small font-weight-bold">Active Profile:</label>
                    <div class="d-flex flex-wrap mb-1">
                      <button 
                        v-for="(profile, key) in reciterProfiles" 
                        :key="key"
                        class="btn btn-sm mr-1 mb-1 flex-fill"
                        :class="selectedProfileKey === key ? 'btn-primary' : 'btn-outline-primary'"
                        @click="switchProfile(key)"
                      >
                        {{ profile.name }}
                      </button>
                    </div>
                    <div class="d-flex flex-wrap">
                      <button 
                        v-for="(profile, key) in basicProfiles" 
                        :key="key"
                        class="btn btn-sm mr-1 mb-1 flex-fill"
                        :class="selectedProfileKey === key ? 'btn-primary' : 'btn-outline-primary'"
                        @click="switchProfile(key)"
                      >
                        {{ profile.name }}
                      </button>
                    </div>
                    <template v-if="aiDetectedProfiles && Object.keys(aiDetectedProfiles).length">
                      <div class="d-flex flex-wrap mt-1">
                        <button 
                          v-for="(profile, key) in aiDetectedProfiles" 
                          :key="key"
                          class="btn btn-sm mr-1 mb-1 flex-fill"
                          :class="selectedProfileKey === key ? 'btn-info' : 'btn-outline-info'"
                          @click="switchProfile(key)"
                        >
                          {{ profile.name }}
                        </button>
                      </div>
                    </template>
                    <small v-if="selectedProfileKey && profiles[selectedProfileKey]" class="text-muted d-block mt-1">
                      {{ profiles[selectedProfileKey].description }}
                    </small>
                  </div>
                </div>

                <div class="card border-info mb-3" v-if="audioFile">
                  <div class="card-body py-2">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <i class="fa fa-lightbulb-o text-info mr-2"></i>
                        <small class="font-weight-bold">AI Recommendation</small>
                      </div>
                      <base-button type="info" size="sm" outline @click="getAiRecommendation" :disabled="recommending">
                        <span v-if="recommending"><i class="fa fa-spinner fa-spin"></i> Analyzing...</span>
                        <span v-else><i class="fa fa-magic"></i> Analyze My Audio</span>
                      </base-button>
                    </div>
                    <div v-if="recommendationAnalysis" class="mt-2">
                      <div class="alert alert-info py-2 mb-0">
                        <small><i class="fa fa-info-circle mr-1"></i> {{ recommendationAnalysis }}</small>
                      </div>
                    </div>
                    <p v-if="recommendError" class="text-danger mt-2 mb-0 small">{{ recommendError }}</p>
                  </div>
                </div>

                <div class="row">
                  <div class="col-lg-8">
                    <div class="effects-chain">
                      <div v-for="moduleKey in processingOrder" :key="moduleKey" class="effect-module card mb-3" :class="{ 'disabled-module': !currentSettings[moduleKey].enabled }">
                        <div class="card-header py-2 d-flex justify-content-between align-items-center" @click="toggleModuleCollapse(moduleKey)" style="cursor: pointer;">
                          <div class="d-flex align-items-center">
                            <div class="custom-control custom-switch mr-3" @click.stop>
                              <input type="checkbox" class="custom-control-input" :id="'toggle-' + moduleKey" v-model="currentSettings[moduleKey].enabled" @change="updatePreview">
                              <label class="custom-control-label" :for="'toggle-' + moduleKey"></label>
                            </div>
                            <i :class="modules[moduleKey].icon + ' mr-2'"></i>
                            <strong>{{ modules[moduleKey].name }}</strong>
                          </div>
                          <i class="fa" :class="collapsedModules[moduleKey] ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                        </div>
                        <div class="card-body py-3" v-show="!collapsedModules[moduleKey] && currentSettings[moduleKey].enabled">
                          <small class="text-muted d-block mb-3">{{ modules[moduleKey].description }}</small>
                          <div v-for="(paramDef, paramKey) in getModuleParams(moduleKey)" :key="paramKey" class="mb-3">
                            <div v-if="paramDef.type !== 'toggle'" class="slider-group">
                              <div class="d-flex justify-content-between mb-1">
                                <label class="mb-0 small font-weight-bold">{{ paramDef.label }}</label>
                                <span class="badge badge-primary">{{ formatParamValue(moduleKey, paramKey, currentSettings[moduleKey][paramKey]) }}</span>
                              </div>
                              <input 
                                type="range" 
                                class="custom-range"
                                :min="paramDef.min" 
                                :max="paramDef.max" 
                                :step="paramDef.step"
                                v-model.number="currentSettings[moduleKey][paramKey]"
                                @input="updatePreview"
                              >
                              <div class="d-flex justify-content-between">
                                <small class="text-muted">{{ paramDef.min }}</small>
                                <small class="text-muted">{{ paramDef.max }}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-lg-4">
                    <div class="card border sticky-top" style="top: 20px;">
                      <div class="card-header bg-dark text-white py-2">
                        <h5 class="mb-0 text-white"><i class="ni ni-headphones mr-1"></i> Audio Preview</h5>
                      </div>
                      <div class="card-body py-3">
                        <div class="mb-3">
                          <label class="small font-weight-bold mb-1">Original Audio</label>
                          <audio ref="origPlayer" :src="audioUrl" controls class="w-100" style="height: 36px;"></audio>
                        </div>

                        <div class="mb-3">
                          <label class="small font-weight-bold mb-1">Preview</label>
                          <div class="preview-controls">
                            <base-button :type="studioPreviewPlaying ? 'danger' : 'info'" size="sm" block @click="toggleStudioPreview" :disabled="!audioFile || studioPreviewLoading">
                              <span v-if="studioPreviewLoading"><i class="fa fa-spinner fa-spin"></i> Rendering...</span>
                              <span v-else><i :class="studioPreviewPlaying ? 'fa fa-stop' : 'fa fa-headphones'"></i> {{ studioPreviewPlaying ? 'Stop Preview' : 'Play Preview' }}</span>
                            </base-button>
                          </div>
                          <small class="text-muted d-block mt-1">Preview covers the first 10 seconds only.</small>
                        </div>

                        <hr>

                        <base-button type="primary" block @click="processAudio" :disabled="processing">
                          <span v-if="processing"><i class="fa fa-spinner fa-spin"></i> Processing...</span>
                          <span v-else><i class="fa fa-cog"></i> Process Audio</span>
                        </base-button>

                        <div v-if="processResult" class="mt-3">
                          <div class="alert alert-success py-2">
                            <small class="d-block"><strong>Processing Complete!</strong></small>
                            <small>Duration: {{ processResult.stats.duration.toFixed(1) }}s</small><br>
                            <small>Peak: {{ processResult.stats.input_peak_db }}dB → {{ processResult.stats.output_peak_db }}dB</small>
                          </div>
                          <audio :src="processedAudioUrl" controls class="w-100 mb-2" style="height: 36px;"></audio>
                          <base-button type="success" block size="sm" @click="downloadProcessed">
                            <i class="fa fa-download"></i> Download WAV
                          </base-button>
                        </div>

                        <div v-if="processError" class="alert alert-danger mt-3 py-2">
                          <small>{{ processError }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioProcessor',
  data() {
    return {
      ready: false,
      loadError: null,
      apiBaseUrl: '',

      currentStep: 1,
      audioFile: null,
      audioUrl: null,
      isDragging: false,

      modules: {},
      processingOrder: [],
      profiles: {},
      selectedProfileKey: null,
      currentSettings: {},
      collapsedModules: {},

      detectingProfile: false,
      detectError: null,
      detectedProfile: null,

      recommending: false,
      recommendError: null,
      recommendationAnalysis: null,

      studioPreviewPlaying: false,
      studioPreviewLoading: false,
      studioPreviewAudio: null,
      studioPreviewUrl: null,
      studioPreviewKey: null,

      processing: false,
      processResult: null,
      processedAudioUrl: null,
      processError: null
    }
  },
  created() {
    this.apiBaseUrl = window.location.origin;
    this.loadModules();
  },
  computed: {
    basicProfiles() {
      const result = {};
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (!profile.category || profile.category === 'Basic') result[key] = profile;
      }
      return result;
    },
    reciterProfiles() {
      const result = {};
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (profile.category === 'Reciter') result[key] = profile;
      }
      return result;
    },
    sortedProfiles() {
      const result = {};
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (profile.category === 'Reciter') result[key] = profile;
      }
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (!profile.category || profile.category === 'Basic') result[key] = profile;
      }
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (profile.category === 'AI Detected' || profile.category === 'AI Recommended') result[key] = profile;
      }
      return result;
    },
    aiDetectedProfiles() {
      const result = {};
      for (const [key, profile] of Object.entries(this.profiles)) {
        if (profile.category === 'AI Detected' || profile.category === 'AI Recommended') result[key] = profile;
      }
      return result;
    }
  },
  beforeDestroy() {
    this.stopStudioPreview();
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    if (this.processedAudioUrl) URL.revokeObjectURL(this.processedAudioUrl);
    if (this.studioPreviewUrl) URL.revokeObjectURL(this.studioPreviewUrl);
  },
  methods: {
    async loadModules() {
      this.loadError = null;
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/audio/modules`);
        this.modules = response.data.modules;
        this.processingOrder = response.data.order;

        const profilesResp = await this.axios.get(`${this.apiBaseUrl}/api/audio/profiles`);
        this.profiles = profilesResp.data.profiles;

        this.ready = true;
        this.initDefaultSettings();
      } catch (err) {
        this.loadError = 'Something went wrong while loading. Please try again.';
        this.ready = false;
      }
    },

    initDefaultSettings() {
      const settings = {};
      const collapsed = {};
      for (const key of this.processingOrder) {
        const mod = this.modules[key];
        if (!mod) continue;
        settings[key] = {};
        collapsed[key] = true;
        for (const [paramKey, paramDef] of Object.entries(mod.params)) {
          settings[key][paramKey] = paramDef.default;
        }
      }
      this.currentSettings = settings;
      this.collapsedModules = collapsed;
    },

    formatParamValue(moduleKey, paramKey, value) {
      if (moduleKey === 'maximizer' && paramKey === 'mode') {
        const modes = ['A (Warm)', 'B (Balanced)', 'C (Bright)', 'D (Aggressive)'];
        return modes[value] || value;
      }
      return value;
    },

    getModuleParams(moduleKey) {
      const params = this.modules[moduleKey]?.params || {};
      const filtered = {};
      for (const [k, v] of Object.entries(params)) {
        if (k !== 'enabled') filtered[k] = v;
      }
      return filtered;
    },

    toggleModuleCollapse(key) {
      this.$set(this.collapsedModules, key, !this.collapsedModules[key]);
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    handleDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0 && files[0].type.startsWith('audio/')) {
        this.setAudioFile(files[0]);
      }
    },

    handleFileSelect(e) {
      if (e.target.files.length > 0) {
        this.setAudioFile(e.target.files[0]);
      }
    },

    async setAudioFile(file) {
      if (file.size > 25 * 1024 * 1024) {
        alert('File too large. Maximum size is 25MB.');
        return;
      }
      this.audioFile = file;
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = URL.createObjectURL(file);
      this.processResult = null;
      this.processedAudioUrl = null;
      this.processError = null;
      this.clearStudioPreview();
    },

    removeFile() {
      this.audioFile = null;
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
      this.processResult = null;
      this.processedAudioUrl = null;
      this.clearStudioPreview();
    },

    goToProfileStep() {
      if (!this.audioFile) return;
      this.currentStep = 2;
    },

    selectProfile(key) {
      this.selectedProfileKey = key;
      const profile = this.profiles[key];
      if (profile && profile.settings) {
        this.applyProfileSettings(profile.settings);
        this.updatePreview();
      }
    },

    applyProfileSettings(settings) {
      for (const moduleKey of this.processingOrder) {
        if (settings[moduleKey]) {
          for (const [param, value] of Object.entries(settings[moduleKey])) {
            this.$set(this.currentSettings[moduleKey], param, value);
          }
        }
      }
    },

    switchProfile(key) {
      this.stopStudioPreview();
      this.selectedProfileKey = key;
      const profile = this.profiles[key];
      if (profile && profile.settings) {
        this.applyProfileSettings(profile.settings);
      }
    },

    resetToProfile() {
      this.stopStudioPreview();
      if (this.selectedProfileKey && this.profiles[this.selectedProfileKey]) {
        this.applyProfileSettings(this.profiles[this.selectedProfileKey].settings);
      }
    },

    goToCustomizeStep() {
      this.currentStep = 3;
      for (const key of this.processingOrder) {
        this.$set(this.collapsedModules, key, false);
      }
    },

    handleReferenceSelect(e) {
      if (e.target.files.length > 0) {
        this.detectProfile(e.target.files[0]);
      }
    },

    async detectProfile(file) {
      this.detectingProfile = true;
      this.detectError = null;
      this.detectedProfile = null;

      const formData = new FormData();
      formData.append('audio', file);

      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/audio/detect-profile`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });

        if (response.data.success) {
          this.detectedProfile = response.data;

          const profileKey = 'ai_detected';
          this.$set(this.profiles, profileKey, {
            name: response.data.profile_name,
            description: response.data.description,
            category: 'AI Detected',
            settings: response.data.settings
          });
          this.selectProfile(profileKey);
        }
      } catch (err) {
        this.detectError = err.response?.data?.detail || 'Profile detection failed';
      }
      this.detectingProfile = false;
    },

    async getAiRecommendation() {
      if (!this.audioFile) return;
      this.recommending = true;
      this.recommendError = null;
      this.recommendationAnalysis = null;
      this.stopStudioPreview();

      const formData = new FormData();
      formData.append('audio', this.audioFile);

      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/audio/recommend`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });

        if (response.data.success) {
          this.recommendationAnalysis = response.data.analysis;

          const profileKey = 'ai_recommended';
          this.$set(this.profiles, profileKey, {
            name: 'AI Recommended',
            description: response.data.analysis,
            category: 'AI Recommended',
            settings: response.data.settings
          });
          this.selectProfile(profileKey);
        }
      } catch (err) {
        this.recommendError = err.response?.data?.detail || 'AI recommendation failed. Please try again.';
      }
      this.recommending = false;
    },

    updatePreview() {
      // Settings changed — any playing preview is now stale
      if (this.studioPreviewPlaying) this.stopStudioPreview();
    },

    async toggleStudioPreview() {
      if (this.studioPreviewPlaying) {
        this.stopStudioPreview();
        return;
      }
      if (!this.audioFile) return;

      // Re-render only when the file or settings changed since last render
      const key = `${this.audioFile.name}:${this.audioFile.size}:${this.audioFile.lastModified}:${JSON.stringify(this.currentSettings)}`;
      if (this.studioPreviewUrl && this.studioPreviewKey === key) {
        this.playStudioPreview();
        return;
      }

      this.studioPreviewLoading = true;
      try {
        const formData = new FormData();
        formData.append('audio', this.audioFile);
        formData.append('settings', JSON.stringify(this.currentSettings));
        const response = await this.axios.post(`${this.apiBaseUrl}/api/audio/preview`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob'
        });
        if (this.studioPreviewUrl) URL.revokeObjectURL(this.studioPreviewUrl);
        this.studioPreviewUrl = URL.createObjectURL(response.data);
        this.studioPreviewKey = key;
        this.playStudioPreview();
      } catch (err) {
        this.processError = 'Studio preview failed. Please try again.';
      }
      this.studioPreviewLoading = false;
    },

    playStudioPreview() {
      if (!this.studioPreviewAudio) {
        this.studioPreviewAudio = new Audio();
        this.studioPreviewAudio.onended = () => { this.studioPreviewPlaying = false; };
      }
      this.studioPreviewAudio.src = this.studioPreviewUrl;
      this.studioPreviewAudio.play();
      this.studioPreviewPlaying = true;
    },

    stopStudioPreview() {
      if (this.studioPreviewAudio) {
        this.studioPreviewAudio.pause();
        this.studioPreviewAudio.currentTime = 0;
      }
      this.studioPreviewPlaying = false;
    },

    clearStudioPreview() {
      this.stopStudioPreview();
      if (this.studioPreviewUrl) URL.revokeObjectURL(this.studioPreviewUrl);
      this.studioPreviewUrl = null;
      this.studioPreviewKey = null;
    },

    async processAudio() {
      if (!this.audioFile) return;
      this.processing = true;
      this.processError = null;
      this.processResult = null;
      if (this.processedAudioUrl) URL.revokeObjectURL(this.processedAudioUrl);
      this.processedAudioUrl = null;

      const formData = new FormData();
      formData.append('audio', this.audioFile);
      formData.append('settings', JSON.stringify(this.currentSettings));

      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/audio/process`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });

        if (response.data.success) {
          const downloadResp = await this.axios.get(
            `${this.apiBaseUrl}/api/audio/download/${response.data.job_id}`,
            {
              responseType: 'blob'
            }
          );
          this.processedAudioUrl = URL.createObjectURL(downloadResp.data);
          this.processing = false;
          this.processResult = response.data;
        } else {
          this.processing = false;
        }
      } catch (err) {
        this.processError = err.response?.data?.detail || 'Processing failed. Please try again.';
        this.processing = false;
      }
    },

    async downloadProcessed() {
      if (!this.processResult) return;
      try {
        const response = await this.axios.get(
          `${this.apiBaseUrl}/api/audio/download/${this.processResult.job_id}`,
          {
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `processed_${this.audioFile.name.replace(/\.[^.]+$/, '')}.wav`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        alert('Download failed');
      }
    }
  }
}
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  transition: all 0.3s;
  cursor: pointer;
}
.upload-zone:hover, .upload-zone.drag-over {
  border-color: #5e72e4;
  background: rgba(94, 114, 228, 0.05);
}

.step-indicators {
  padding: 0 20px;
}
.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}
.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #dee2e6;
  color: #8898aa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 4px;
  transition: all 0.3s;
}
.step-item.active .step-circle {
  background: #5e72e4;
  color: white;
}
.step-item.completed .step-circle {
  background: #2dce89;
  color: white;
}
.step-line {
  flex: 1;
  height: 2px;
  background: #dee2e6;
  margin-top: 18px;
  max-width: 120px;
  transition: all 0.3s;
}
.step-line.active {
  background: #2dce89;
}

.profile-card {
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}
.profile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.profile-card.selected {
  border-color: #5e72e4;
  box-shadow: 0 4px 12px rgba(94, 114, 228, 0.3);
}

.effect-module {
  transition: all 0.2s;
}
.effect-module.disabled-module {
  opacity: 0.5;
}

.custom-range {
  width: 100%;
}
.custom-range::-webkit-slider-thumb {
  background: #5e72e4;
}

.slider-group {
  padding: 0 4px;
}

.preview-controls {
  margin-top: 4px;
}
</style>
